import { cbAiApi, UserFeedback } from '@/services/aero/api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useExplorer } from '../useExplorer';
import { useFetchUser } from '@/auth/hooks';
import { useCodeGenerationStore } from '@/stores/useCodeGenerationStore';
import type { AiRoutingContext } from '@/services/aero/cbAiApi';
import { resolveAiApiRoot } from '@/services/aero/aiHostResolver';
import { config } from '@/config';

const DEFAULT_SDK = 'node';

export default function useCodeGenerator() {
  const { siteName } = useExplorer();
  const {
    isGenerating,
    aiResponse,
    messages,
    currentSessionId,
    conversationVersion,
    setIsGenerating,
    setAiResponseOperationIds,
    setStatusMessage,
    setCurrentSessionId,
    addChainOfThoughtEvent,
    setCurrentMessageId,
    clearConversation: clearStoreConversation,
    updateLatestAssistantMessage,
    addUserMessage,
    addAssistantMessage,
  } = useCodeGenerationStore();

  const [logId, setLogId] = useState<string | null>(null);
  const { user, isLoggedIn } = useFetchUser();
  const tenantName = siteName || user?.active_site?.site_name;

  const context = useMemo(
    () => ({
      email: user?.email || '',
      siteName: tenantName || '',
    }),
    [tenantName, user?.email]
  );

  const routingContext = useMemo<AiRoutingContext>(
    () => ({
      isAuthenticated: Boolean(isLoggedIn),
      tenantName,
      userToken: user?.token,
    }),
    [isLoggedIn, tenantName, user?.token]
  );
  const resolvedAiApiRoot = useMemo(
    () => resolveAiApiRoot(config.cbAiApiUrl, routingContext),
    [routingContext]
  );
  const [output, setOutput] = useState(aiResponse || '');
  const [isStreamFinished, setIsStreamFinished] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  const activeRequestIdRef = useRef(0);
  const activeRequestConversationVersionRef = useRef<number | null>(null);
  const prevConversationVersionRef = useRef(conversationVersion);
  const prevAiApiRootRef = useRef<string | null>(null);

  // Abort/invalidate in-flight stream whenever conversation is externally
  // reset (site switch, logout, reset-to-home, etc.).
  //
  // We only abort if the active request started on an older conversationVersion.
  // This avoids aborting a fresh request that may start immediately after reset
  // but before this effect flushes.
  useEffect(() => {
    if (prevConversationVersionRef.current !== conversationVersion) {
      const activeRequestConversationVersion =
        activeRequestConversationVersionRef.current;
      const hasStaleActiveRequest =
        activeRequestConversationVersion !== null &&
        activeRequestConversationVersion < conversationVersion;

      if (hasStaleActiveRequest) {
        activeRequestIdRef.current += 1;
        activeRequestConversationVersionRef.current = null;
        abortControllerRef.current?.abort();
      }
    }

    prevConversationVersionRef.current = conversationVersion;
  }, [conversationVersion]);

  // Handle different event types from the streaming API
  const handleEvent = useCallback(
    /* eslint-disable @typescript-eslint/no-explicit-any */
    async (
      eventType: string,
      payload: Record<string, any>,
      requestId: number
    ) => {
      // Ignore stale events from an invalidated stream (e.g. after logout/site switch)
      if (requestId !== activeRequestIdRef.current) return;

      switch (eventType) {
        case 'status': {
          // Display actual server message if available, with fallback for backward compatibility
          const message =
            payload?.message ?? payload?.content ?? 'Processing...';
          setStatusMessage?.(
            typeof message === 'string' ? message : 'Processing...'
          );

          // Track chain of thought event
          const currentMessageId =
            useCodeGenerationStore.getState().currentMessageId;
          if (currentMessageId) {
            addChainOfThoughtEvent?.(currentMessageId, {
              type: 'status',
              message: typeof message === 'string' ? message : 'Processing...',
              data: payload,
            });
          }
          break;
        }
        case 'content_update': {
          if (payload?.content) {
            setOutput(payload.content as string);
            updateLatestAssistantMessage(payload.content as string);
          }
          setAiResponseOperationIds(payload?.operationIds || []);

          // Track chain of thought event
          const currentMessageId =
            useCodeGenerationStore.getState().currentMessageId;
          if (currentMessageId) {
            addChainOfThoughtEvent?.(currentMessageId, {
              type: 'content_update',
              data: payload,
            });
          }
          break;
        }
        case 'done': {
          const content = payload?.content || payload?.message || '';

          if (content) {
            setOutput(content as string);
            updateLatestAssistantMessage(content as string);
          }

          if (payload?.logId) {
            setLogId(payload.logId);
          }
          setIsGenerating(false);
          setIsStreamFinished(true);

          // Track chain of thought event
          const currentMessageId =
            useCodeGenerationStore.getState().currentMessageId;
          if (currentMessageId) {
            addChainOfThoughtEvent?.(currentMessageId, {
              type: 'done',
              data: payload,
            });
          }
          break;
        }
        case 'task-update': {
          // Task updates may contain final messages in the messages array
          if (payload?.messages && Array.isArray(payload.messages)) {
            const messages = payload.messages;
            // Look for assistant messages that contain final content
            const assistantMessage = messages.find(
              (msg: any) =>
                msg.role === 'assistant' &&
                msg.content &&
                msg.content.trim().length > 0
            );

            if (assistantMessage?.content) {
              const content = assistantMessage.content;
              setOutput(content);
              updateLatestAssistantMessage(content);

              // Mark as finished
              setIsGenerating(false);
              setIsStreamFinished(true);
            }
          }

          // Track chain of thought event
          const currentMessageId =
            useCodeGenerationStore.getState().currentMessageId;
          if (currentMessageId) {
            addChainOfThoughtEvent?.(currentMessageId, {
              type: 'task-update',
              data: payload,
            });
          }
          break;
        }
        case 'tool-update': {
          // Tool updates are typically status messages
          if (payload?.message) {
            setStatusMessage?.(payload.message);
          }

          // Track chain of thought event
          const currentMessageId =
            useCodeGenerationStore.getState().currentMessageId;
          if (currentMessageId) {
            addChainOfThoughtEvent?.(currentMessageId, {
              type: 'tool-update',
              message: payload?.message,
              data: payload,
            });
          }
          break;
        }
        case 'final-output': {
          // Handle final output from the API
          const content = payload?.content || payload?.message || '';

          if (content) {
            setOutput(content as string);
            updateLatestAssistantMessage(content as string);
          }

          if (payload?.logId) {
            setLogId(payload.logId);
          }

          setIsGenerating(false);
          setIsStreamFinished(true);

          // Track chain of thought event
          const currentMessageId =
            useCodeGenerationStore.getState().currentMessageId;
          if (currentMessageId) {
            addChainOfThoughtEvent?.(currentMessageId, {
              type: 'final-output',
              data: payload,
            });
          }
          break;
        }
        case 'error': {
          const errorMessage =
            payload?.message ??
            'Unable to generate code at this time. Please try again.';
          setOutput(errorMessage);
          updateLatestAssistantMessage(errorMessage);
          setIsGenerating(false);
          setIsStreamFinished(true);

          // Track chain of thought event
          const currentMessageId =
            useCodeGenerationStore.getState().currentMessageId;
          if (currentMessageId) {
            addChainOfThoughtEvent?.(currentMessageId, {
              type: 'error',
              message: errorMessage,
              data: payload,
            });
          }
          break;
        }
        default: {
          // Unhandled event type - can be safely ignored
          break;
        }
      }
    },
    [
      setStatusMessage,
      updateLatestAssistantMessage,
      setIsGenerating,
      addChainOfThoughtEvent,
      setAiResponseOperationIds,
    ]
  );

  const handleGenerate = useCallback(
    async (prompt: string, sdk?: string | null) => {
      if (!prompt.trim()) return;

      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;
      const generationConversationVersion =
        useCodeGenerationStore.getState().conversationVersion;
      activeRequestConversationVersionRef.current =
        generationConversationVersion;

      setIsGenerating(true);
      setIsStreamFinished(false);
      setOutput('');
      setLogId(null);
      setAiResponseOperationIds([]);
      setStatusMessage?.('Analyzing your request...');

      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // Add a timeout to prevent indefinite hanging
      const timeoutId = setTimeout(() => {
        if (requestId === activeRequestIdRef.current) {
          abortControllerRef.current?.abort();
        }
      }, 120000); // 2 minute timeout

      try {
        for await (const resp of cbAiApi.codeGenerationStream(
          prompt,
          sdk || DEFAULT_SDK,
          context,
          signal,
          currentSessionId,
          routingContext
        )) {
          if (signal.aborted || requestId !== activeRequestIdRef.current) {
            break;
          }

          // Conversation was reset while this request was in flight
          if (
            useCodeGenerationStore.getState().conversationVersion !==
            generationConversationVersion
          ) {
            abortControllerRef.current?.abort();
            break;
          }

          const { eventType, payload } = resp;

          // Track session_created event for chain of thought and update store
          if (eventType === 'session_created') {
            const sessionId = (payload as { sessionId?: string })?.sessionId;
            if (sessionId) {
              setCurrentSessionId(sessionId);
            }

            const currentMessageId =
              useCodeGenerationStore.getState().currentMessageId;
            if (currentMessageId) {
              addChainOfThoughtEvent?.(currentMessageId, {
                type: 'session_created',
                data: payload,
              });
            }
          }

          await handleEvent(
            eventType,
            payload as Record<string, string>,
            requestId
          );
        }
      } catch (error) {
        if (requestId !== activeRequestIdRef.current) {
          return;
        }

        if ((error as Error).name !== 'AbortError') {
          // Error generating code - handled by error boundary
          setOutput('Unable to generate code at this time. Please try again.');

          // Update the latest assistant message with error
          updateLatestAssistantMessage(
            'Unable to generate code at this time. Please try again.'
          );
        }
      } finally {
        clearTimeout(timeoutId);

        if (requestId !== activeRequestIdRef.current) {
          return;
        }

        activeRequestConversationVersionRef.current = null;
        setIsGenerating(false);
        setIsStreamFinished(true);
      }
    },
    [
      handleEvent,
      setStatusMessage,
      context,
      updateLatestAssistantMessage,
      setIsGenerating,
      setAiResponseOperationIds,
      addChainOfThoughtEvent,
      currentSessionId,
      setCurrentSessionId,
      routingContext,
    ]
  );

  const sendMessage = useCallback(
    (prompt: string, _isFirstMessage: boolean = false, sdk?: string | null) => {
      // Add user message
      addUserMessage(prompt);

      // Add assistant message placeholder
      addAssistantMessage('');

      // Get the ID of the assistant message that was just added
      const messages = useCodeGenerationStore.getState().messages;
      const latestAssistantMessage = messages
        .filter((m: { role: string }) => m.role === 'assistant')
        .pop();

      if (latestAssistantMessage) {
        // Set this as the current message ID for events
        setCurrentMessageId(latestAssistantMessage.id);
      }

      setIsGenerating(true);

      // Start generation
      handleGenerate(prompt, sdk);
    },
    [
      addUserMessage,
      addAssistantMessage,
      setIsGenerating,
      handleGenerate,
      setCurrentMessageId,
    ]
  );

  const clearConversation = useCallback(() => {
    activeRequestIdRef.current += 1;
    activeRequestConversationVersionRef.current = null;
    abortControllerRef.current?.abort();

    clearStoreConversation();
    setOutput('');
    setLogId(null);
    setIsStreamFinished(true);
  }, [clearStoreConversation]);

  // If resolved AI host changes (site/token/region shift), invalidate the
  // current conversation/session so stale session IDs are not reused on a new host.
  useEffect(() => {
    const prevRoot = prevAiApiRootRef.current;
    prevAiApiRootRef.current = resolvedAiApiRoot;

    if (!prevRoot || prevRoot === resolvedAiApiRoot) {
      return;
    }

    if (currentSessionId || messages.length > 0) {
      clearConversation();
    }
  }, [resolvedAiApiRoot, currentSessionId, messages.length, clearConversation]);

  const abortStream = useCallback(() => {
    activeRequestIdRef.current += 1;
    activeRequestConversationVersionRef.current = null;
    abortControllerRef.current?.abort();

    setOutput('');
    setIsGenerating(false);
    setIsStreamFinished(true);
  }, [setIsGenerating]);

  const submitUserFeedback = useCallback(
    (feedback: UserFeedback) => {
      if (logId && feedback) {
        return cbAiApi.submitUserFeedback(logId, feedback, routingContext);
      }
      return Promise.resolve(false);
    },
    [logId, routingContext]
  );

  return {
    output,
    logId,
    setOutput,
    isStreamFinished,
    setIsStreamFinished,
    isGenerating,
    handleGenerate,
    sendMessage,
    clearConversation,
    abortStream,
    submitUserFeedback,
  };
}
